// Web Vitals Monitoring
// Tracks Core Web Vitals and reports to Google Analytics 4

import { onCLS, onINP, onLCP, onFCP, onTTFB, type Metric } from 'web-vitals';

/**
 * Reports a Web Vitals metric to Google Analytics 4
 */
function reportMetric(metric: Metric): void {
  // Only report if GA4 is available
  if (typeof window !== 'undefined' && window.gtag) {
    // Send custom event to GA4
    window.gtag('event', metric.name, {
      value: Math.round(metric.name === 'CLS' ? metric.value * 1000 : metric.value),
      metric_id: metric.id,
      metric_value: metric.value,
      metric_delta: metric.delta,
      metric_rating: metric.rating, // 'good', 'needs-improvement', or 'poor'
      event_category: 'Web Vitals',
    });

    // Log in development for debugging
    if (import.meta.env.DEV) {
      console.log(`[Web Vitals] ${metric.name}:`, {
        value: metric.value,
        rating: metric.rating,
        delta: metric.delta,
      });
    }
  }
}

/**
 * Initialize Web Vitals monitoring
 * Call this once in your app's entry point (main.tsx)
 */
export function initWebVitals(): void {
  // Core Web Vitals
  onCLS(reportMetric);  // Cumulative Layout Shift
  onINP(reportMetric);  // Interaction to Next Paint (replaces FID in web-vitals v4)
  onLCP(reportMetric);  // Largest Contentful Paint

  // Other Web Vitals
  onFCP(reportMetric);  // First Contentful Paint
  onTTFB(reportMetric); // Time to First Byte

  if (import.meta.env.DEV) {
    console.log('[Web Vitals] Monitoring initialized');
  }
}

/**
 * Web Vitals thresholds for reference
 *
 * LCP (Largest Contentful Paint):
 *   - Good: ≤ 2.5s
 *   - Needs Improvement: 2.5s - 4s
 *   - Poor: > 4s
 *
 * INP (Interaction to Next Paint):
 *   - Good: ≤ 200ms
 *   - Needs Improvement: 200ms - 500ms
 *   - Poor: > 500ms
 *
 * CLS (Cumulative Layout Shift):
 *   - Good: ≤ 0.1
 *   - Needs Improvement: 0.1 - 0.25
 *   - Poor: > 0.25
 *
 * FCP (First Contentful Paint):
 *   - Good: ≤ 1.8s
 *   - Needs Improvement: 1.8s - 3s
 *   - Poor: > 3s
 *
 * TTFB (Time to First Byte):
 *   - Good: ≤ 800ms
 *   - Needs Improvement: 800ms - 1800ms
 *   - Poor: > 1800ms
 */
